/**
 * "These two are the same company."
 *
 * A screen under the client list rather than a row in the navigation, because
 * on a good day it is empty, and a permanent nav entry that is usually empty
 * reads as a broken feature. It is reached from the client list, which is where
 * somebody notices the problem in the first place.
 *
 * Every pair says which rule found it. That is not decoration: a shared registry
 * code is proof, a shared name is a strong hint, and a shared e-mail is often
 * just the accountant. An operator merging two records is doing something that
 * cannot be undone by a button, so the screen has to hand over its own
 * uncertainty rather than present three different findings as one verdict.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ChevronLeft, CopyCheck, Loader2, UserSearch } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  getDuplicates, mergeDuplicate,
  type DuplicatePair, type DuplicateScan, type MergeOutcome, type PartyRef,
} from "../../../lib/duplicates";
import { Btn, EmptyState, Modal, PageHead, useToast } from "../../ui";

/** Both sides of a pair link to the record itself: a decision about two
 *  companies cannot be made from two names alone. */
function PartyLink({ party, label }: { party: PartyRef; label: string }) {
  const Icon = party.kind === "client" ? Building2 : UserSearch;
  return (
    <Link
      to={`/${party.kind === "client" ? "clients" : "leads"}/${party.id}`}
      className="group flex min-w-0 items-center gap-2"
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate font-[650] group-hover:underline">{party.name}</span>
        <span className="block text-xs text-muted-foreground">{label}</span>
      </span>
    </Link>
  );
}

export function DuplicatesPage({ user }: { user: StaffUser }) {
  const t = useT();
  const nav = useNavigate();
  const toast = useToast();
  const canManage = userCanPlatform(user, "manage:crm.clients");

  const [scan, setScan] = useState<DuplicateScan | null>(null);
  const [loading, setLoading] = useState(true);
  const [pair, setPair] = useState<DuplicatePair | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setScan(await getDuplicates());
    } catch {
      toast(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => { void load(); }, [load]);

  const pairs = scan?.pairs ?? [];

  return (
    <>
      <PageHead
        title={t("duplicates.title")}
        sub={
          scan
            ? `${t("duplicates.scanned", { leads: scan.scanned.leads, clients: scan.scanned.clients })} ${t("duplicates.found", { n: pairs.length })}`
            : t("common.loading")
        }
        actions={
          <Btn onClick={() => nav("/clients")}>
            <ChevronLeft className="size-4" />
            {t("duplicates.back")}
          </Btn>
        }
      />

      <div className="crm-card p-0">
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : pairs.length === 0 ? (
          <EmptyState icon={CopyCheck} title={t("duplicates.empty")} body={t("duplicates.emptyBody")} />
        ) : (
          <ul className="divide-y divide-border">
            {pairs.map((p) => (
              <li key={p.key} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-[180px] flex-1">
                  <PartyLink party={p.a} label={t(`duplicates.kind.${p.a.kind}`)} />
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-1">
                  {p.rules.map((rule) => (
                    <span
                      key={rule}
                      className={
                        rule === "reg_code"
                          ? "rounded-full bg-emerald-500/12 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400"
                          : rule === "name"
                            ? "rounded-full bg-amber-500/12 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                            : "rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                      }
                    >
                      {t(`duplicates.rule.${rule}`)}
                    </span>
                  ))}
                </div>
                <div className="min-w-[180px] flex-1">
                  <PartyLink party={p.b} label={t(`duplicates.kind.${p.b.kind}`)} />
                </div>
                {canManage ? (
                  <Btn small tone="dark" onClick={() => setPair(p)}>{t("duplicates.merge")}</Btn>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* What the scan refused to expand. Saying it out loud is the difference
          between "nothing found" and "one address is on nine companies". */}
      {scan?.skipped.length ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {t("duplicates.skipped", {
            list: scan.skipped.map((s) => `${s.value} (${s.size})`).join(", "),
          })}
        </p>
      ) : null}
      {scan?.truncated ? (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{t("duplicates.truncated")}</p>
      ) : null}

      {pair ? (
        <MergeDialog
          pair={pair}
          onClose={() => setPair(null)}
          onDone={() => { setPair(null); void load(); }}
        />
      ) : null}
    </>
  );
}

/**
 * Choosing which record survives, and then saying what actually happened.
 *
 * The dialog does not close on success. A merge moves rows between records and
 * closes one of them, and "done" with no account of what moved is exactly the
 * kind of answer that makes somebody afraid to press the button again.
 */
function MergeDialog({
  pair,
  onClose,
  onDone,
}: {
  pair: DuplicatePair;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  // A client always outranks a lead: it is the record carrying invoices,
  // projects and a portal account. When the pair is mixed there is nothing to
  // choose, and the dialog says so rather than offering a choice it would refuse.
  const mixed = pair.a.kind !== pair.b.kind;
  const forced = mixed ? (pair.a.kind === "client" ? pair.a : pair.b) : null;
  const [keepId, setKeepId] = useState<string>(forced?.id ?? pair.a.id);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<MergeOutcome | null>(null);

  const keep = pair.a.id === keepId ? pair.a : pair.b;
  const merge = pair.a.id === keepId ? pair.b : pair.a;

  async function run() {
    setBusy(true);
    try {
      setOutcome(await mergeDuplicate({
        keep: { kind: keep.kind, id: keep.id },
        merge: { kind: merge.kind, id: merge.id },
      }));
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  if (outcome) {
    const moved = outcome.moves.reduce((n, m) => n + m.moved, 0);
    const kept = outcome.moves.reduce((n, m) => n + m.kept, 0);
    return (
      <Modal title={t("duplicates.doneTitle")} label={t("duplicates.doneTitle")} onClose={onDone}>
        <ul className="space-y-1.5 text-sm">
          <li>{t("duplicates.doneMoved", { n: moved, name: outcome.keep.name })}</li>
          {kept > 0 ? <li className="text-muted-foreground">{t("duplicates.doneKept", { n: kept })}</li> : null}
          {outcome.filled.length ? (
            <li className="text-muted-foreground">
              {t("duplicates.doneFilled", { fields: outcome.filled.join(", ") })}
            </li>
          ) : null}
          {outcome.tagsAdded.length ? (
            <li className="text-muted-foreground">
              {t("duplicates.doneTags", { tags: outcome.tagsAdded.join(", ") })}
            </li>
          ) : null}
          <li className="text-muted-foreground">
            {t(outcome.closedAs === "lost" ? "duplicates.doneClosedLead" : "duplicates.doneClosedClient", {
              name: outcome.merged.name,
            })}
          </li>
        </ul>
        <div className="crm-m-foot">
          <Btn tone="dark" onClick={onDone}>{t("common.close")}</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t("duplicates.mergeTitle")} label={t("duplicates.mergeTitle")} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{t("duplicates.keepWhich")}</p>
      <div className="mt-3 space-y-2">
        {[pair.a, pair.b].map((side) => {
          const disabled = mixed && side.kind === "lead";
          const on = side.id === keepId;
          return (
            <button
              key={`${side.kind}:${side.id}`}
              type="button"
              disabled={disabled}
              onClick={() => setKeepId(side.id)}
              className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm ${
                on ? "border-primary bg-primary/5 font-[650]" : "border-border"
              } ${disabled ? "opacity-45" : "hover:border-primary/60"}`}
            >
              {side.kind === "client" ? <Building2 className="size-4" /> : <UserSearch className="size-4" />}
              <span className="min-w-0 truncate">{side.name}</span>
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                {t(`duplicates.kind.${side.kind}`)}
              </span>
            </button>
          );
        })}
      </div>
      {mixed ? <p className="mt-2 text-xs text-muted-foreground">{t("duplicates.clientWins")}</p> : null}
      <p className="mt-3 text-sm">{t("duplicates.explain", { keep: keep.name, merge: merge.name })}</p>

      <div className="crm-m-foot">
        <Btn onClick={onClose} disabled={busy}>{t("common.cancel")}</Btn>
        <Btn tone="dark" onClick={run} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {t("duplicates.merge")}
        </Btn>
      </div>
    </Modal>
  );
}
